// Utilidades para cotejar documentos con viviendas existentes por dirección (FASE 6 · Hogar).

const STOP = new Set([
  "calle", "c", "c/", "avda", "avenida", "av", "paseo", "plaza", "pza", "camino", "carretera", "ctra",
  "portal", "puerta", "planta", "piso", "bloque", "escalera", "esc", "num", "numero", "nº", "n",
  "de", "del", "la", "el", "los", "las", "y", "bajo", "s/n",
]);

const norm = (v) =>
  (v == null ? "" : String(v))
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9ñ\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getVal = (obj, k) => (obj && obj[k] && obj[k].valor != null ? obj[k].valor : null);

const words = (s) => norm(s).split(" ").filter(Boolean);
const streetWords = (s) => words(s).filter((w) => !STOP.has(w) && isNaN(Number(w)) && w.length >= 3);
const numbers = (s) => words(s).filter((w) => /^\d+$/.test(w) && w.length <= 4);
const extractCP = (s) => {
  const m = norm(s).match(/\b(\d{5})\b/);
  return m ? m[1] : null;
};

// Extrae los datos de vivienda del análisis, con la dirección principal priorizada.
export const extractViviendaData = (analisis) => {
  const v = (analisis && analisis.vivienda) || {};
  const dirSuministro = getVal(v, "direccion_suministro");
  const dirServicio = getVal(v, "direccion_servicio");
  const inmuebleAseg = getVal(v, "inmueble_asegurado");
  const dirContractual = getVal(v, "direccion_contractual");
  const primaryAddress = dirSuministro || dirServicio || inmuebleAseg || dirContractual || null;
  const cp = getVal(v, "codigo_postal") || extractCP(primaryAddress || "");
  return {
    tipo_suministro: getVal(v, "tipo_suministro"),
    direccion_suministro: dirSuministro,
    direccion_servicio: dirServicio,
    inmueble_asegurado: inmuebleAseg,
    direccion_contractual: dirContractual,
    primaryAddress,
    municipio: getVal(v, "municipio"),
    provincia: getVal(v, "provincia"),
    codigo_postal: cp,
    referencia_catastral: getVal(v, "referencia_catastral"),
    aseguradora: getVal(v, "aseguradora"),
    numero_poliza: getVal(v, "numero_poliza"),
  };
};

export const hasViviendaData = (analisis) => {
  const d = extractViviendaData(analisis);
  return !!(d.primaryAddress || d.referencia_catastral || d.codigo_postal);
};

// Compara los datos extraídos con una vivienda concreta.
export const scoreVivienda = (data, viv) => {
  const reasons = [];
  let strong = false;

  // Referencia catastral: coincidencia muy fuerte.
  if (data.referencia_catastral && viv.referencia_catastral &&
      norm(data.referencia_catastral) === norm(viv.referencia_catastral)) {
    reasons.push("referencia catastral");
    strong = true;
  }

  const vivCP = viv.codigo_postal || extractCP(viv.direccion || "");
  const cpEqual = data.codigo_postal && vivCP && String(data.codigo_postal) === String(vivCP);
  if (cpEqual) reasons.push("código postal");

  const munEqual = data.municipio && viv.municipio && norm(data.municipio) === norm(viv.municipio);
  if (munEqual) reasons.push("municipio");

  // Solapamiento de palabras de CALLE (solo la dirección, no municipio/provincia,
  // para que la ciudad no genere falsos positivos).
  const dWords = new Set(streetWords(data.primaryAddress || ""));
  const vWords = streetWords(viv.direccion || "");
  const overlap = vWords.filter((w) => dWords.has(w));
  const numEqual = numbers(data.primaryAddress || "").some((n) => numbers(viv.direccion || "").includes(n));
  if (overlap.length >= 1) reasons.push("dirección");

  if (reasons.includes("dirección") && (cpEqual || numEqual)) strong = true;

  let confidence = null;
  if (strong) confidence = "alta";
  else if (reasons.includes("dirección")) confidence = "media";
  else if (cpEqual || munEqual) confidence = "baja";

  const score = (strong ? 100 : 0) + overlap.length + (cpEqual ? 2 : 0) + (munEqual ? 1 : 0) + (numEqual ? 1 : 0);
  return { reasons, strong, confidence, score };
};

// Devuelve candidatos ordenados y el mejor match.
export const matchViviendas = (analisis, viviendas) => {
  const data = extractViviendaData(analisis);
  if (!hasViviendaData(analisis) || !viviendas || viviendas.length === 0) return { data, candidates: [], best: null };
  const candidates = viviendas
    .map((viv) => ({ viv, ...scoreVivienda(data, viv) }))
    .filter((c) => c.reasons.length > 0 && c.confidence)
    .sort((a, b) => b.score - a.score);
  return { data, candidates, best: candidates[0] || null };
};

export const VIVIENDA_FIELD_LABELS = {
  direccion: "Dirección",
  municipio: "Municipio",
  provincia: "Provincia",
  codigo_postal: "Código postal",
  referencia_catastral: "Referencia catastral",
  aseguradora_hogar: "Aseguradora del hogar",
  numero_poliza: "Nº de póliza",
};

// Calcula cambios propuestos (nuevos y conflictos) entre la ficha de vivienda y el documento.
export const computeViviendaUpdates = (data, viv) => {
  const map = {
    direccion: data.primaryAddress,
    municipio: data.municipio,
    provincia: data.provincia,
    codigo_postal: data.codigo_postal,
    referencia_catastral: data.referencia_catastral,
    aseguradora_hogar: data.aseguradora,
    numero_poliza: data.numero_poliza,
  };
  const changes = [];
  Object.entries(map).forEach(([field, nuevo]) => {
    if (nuevo == null || String(nuevo).trim() === "") return;
    const actual = viv[field];
    if (actual == null || String(actual).trim() === "") {
      changes.push({ field, label: VIVIENDA_FIELD_LABELS[field], nuevo, actual: null, conflicto: false });
    } else if (norm(actual) !== norm(nuevo)) {
      changes.push({ field, label: VIVIENDA_FIELD_LABELS[field], nuevo, actual, conflicto: true });
    }
  });
  return changes;
};
